<template>
  <div>
    <!-- ============ BI 17: busca e distribuição de painéis ============ -->
    <c-panel title="Painéis — busca e distribuição" titleIcon="fas fa-search">
      <el-input v-model="busca" placeholder="Buscar painel por nome ou item do edital"
                prefix-icon="el-icon-search" size="small" clearable
                class="u-mb-10" data-test="campo-busca"></el-input>

      <div v-if="carregandoPaineis" class="estado"><i class="el-icon-loading"></i> carregando…</div>
      <div v-else-if="erroPaineis" class="estado erro" data-test="erro-paineis">{{ erroPaineis }}</div>
      <div v-else-if="!paineisFiltrados.length" class="estado" data-test="busca-vazia">
        Nenhum painel corresponde a “{{ busca }}”.
      </div>
      <table v-else class="table table-striped" data-test="tabela-paineis">
        <thead><tr><th>Painel</th><th>Item</th><th style="width:230px">Ações</th></tr></thead>
        <tbody>
          <tr v-for="p in paineisFiltrados" :key="p.slug">
            <td>{{ p.nome }}<div class="chave">{{ p.slug }}</div></td>
            <td>{{ p.item }}</td>
            <td>
              <el-button type="text" size="mini" @click="abrirConcessao(p)"
                         data-test="btn-conceder">conceder acesso</el-button>
              <el-button class="u-ml-10" type="text" size="mini" @click="abrirCompartilhar(p)"
                         data-test="btn-link">gerar link</el-button>
            </td>
          </tr>
        </tbody>
      </table>
    </c-panel>

    <!-- ============ BI 10: concessões vigentes ============ -->
    <c-panel title="Acessos concedidos por painel" titleIcon="fas fa-user-shield" class="u-mt-10">
      <el-alert type="info" :closable="false" show-icon class="u-mb-10">
        <div slot="title">A concessão só pode <b>estreitar</b>, nunca ampliar</div>
        <div class="obs">
          O conjunto de instituições visíveis é a <b>interseção</b> entre o vínculo do usuário
          (autorização principal) e o que for concedido aqui. Conceder uma escola fora do
          vínculo não dá acesso a ela — a regra é aplicada no servidor.
        </div>
      </el-alert>

      <div v-if="carregandoAcessos" class="estado"><i class="el-icon-loading"></i> carregando…</div>
      <div v-else-if="!acessos.length" class="estado" data-test="acessos-vazio">
        Nenhuma restrição por painel cadastrada — vale a autorização principal.
      </div>
      <table v-else class="table table-striped" data-test="tabela-acessos">
        <thead><tr><th>Painel</th><th>Usuário</th><th>Tipo</th><th>Instituição</th><th></th></tr></thead>
        <tbody>
          <tr v-for="a in acessos" :key="a.id">
            <td>{{ a.painel_slug }}</td>
            <td>{{ a.user_id || '—' }}</td>
            <td>{{ a.tipo_usuario === null ? '—' : a.tipo_usuario }}</td>
            <td>{{ a.instituicao_id || 'todas do vínculo' }}</td>
            <td><el-button type="text" size="mini" @click="revogar(a.id)">revogar</el-button></td>
          </tr>
        </tbody>
      </table>
    </c-panel>

    <!-- ============ BI 11/17: links compartilhados ============ -->
    <c-panel title="Links compartilhados" titleIcon="fas fa-link" class="u-mt-10">
      <div v-if="!links.length" class="estado" data-test="links-vazio">Nenhum link gerado.</div>
      <table v-else class="table table-striped" data-test="tabela-links">
        <thead><tr><th>Painel</th><th>Expira em</th><th>Acessos</th><th>Situação</th><th></th></tr></thead>
        <tbody>
          <tr v-for="l in links" :key="l.token">
            <td>{{ l.painel_slug }}</td>
            <td>{{ l.expira_em }}</td>
            <td>{{ l.acessos }}</td>
            <td>
              <el-tag size="mini" :type="situacaoLink(l).tipo">{{ situacaoLink(l).texto }}</el-tag>
            </td>
            <td>
              <el-button v-if="!l.revogado_em" type="text" size="mini"
                         @click="revogarLink(l.token)">revogar</el-button>
            </td>
          </tr>
        </tbody>
      </table>
    </c-panel>

    <!-- ============ BI 11: monitoramento por limiar ============ -->
    <c-panel title="Monitoramento — alerta por limiar" titleIcon="fas fa-bell" class="u-mt-10">
      <el-alert type="info" :closable="false" show-icon class="u-mb-10">
        <div slot="title">Monitorar não é exportar</div>
        <div class="obs">
          O alerta faz o sistema <b>observar sozinho</b> uma métrica e avisar quando ela cruza
          o limite — é o quinto verbo do item 11 do edital.
        </div>
      </el-alert>

      <el-form :inline="true" size="small">
        <el-form-item label="Métrica">
          <el-select v-model="alerta.metrica" placeholder="selecione" style="width:220px"
                     data-test="alerta-metrica">
            <el-option v-for="m in metricas" :key="m.chave" :label="m.rotulo" :value="m.chave"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Condição">
          <el-select v-model="alerta.operador" style="width:90px">
            <el-option v-for="o in operadores" :key="o" :label="o" :value="o"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Limiar">
          <el-input v-model="alerta.limiar" style="width:110px" data-test="alerta-limiar"></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="avaliandoAlerta" @click="avaliarAlerta"
                     data-test="btn-avaliar-alerta">AVALIAR AGORA</el-button>
        </el-form-item>
      </el-form>

      <el-alert v-if="resultadoAlerta" :type="resultadoAlerta.disparou ? 'warning' : 'success'"
                :closable="false" show-icon data-test="resultado-alerta">
        <div slot="title">
          {{ resultadoAlerta.disparou ? 'ALERTA DISPARADO' : 'Dentro do esperado' }}
        </div>
        <div class="obs">
          {{ resultadoAlerta.metrica }} = {{ resultadoAlerta.valor }}
          (limiar {{ resultadoAlerta.operador }} {{ resultadoAlerta.limiar }})
        </div>
      </el-alert>

      <el-alert v-if="erroAlerta" type="error" :closable="false" show-icon
                data-test="erro-alerta" :title="erroAlerta"></el-alert>

      <div class="u-mt-10">
        <el-checkbox v-model="alerta.recorrente" data-test="alerta-recorrente">
          Avaliar diariamente e notificar quando disparar
        </el-checkbox>
        <div class="obs" v-if="alerta.recorrente">
          A recorrência é executada pelo agendador do servidor (<code>bi:carga</code> diária,
          03:30). Esta tela registra a intenção; o disparo é do backend.
        </div>
      </div>
    </c-panel>

    <!-- concessão -->
    <el-dialog :title="'Conceder acesso — ' + (painelAtual ? painelAtual.nome : '')"
               :visible.sync="dialogConcessao" width="460px" append-to-body>
      <el-form label-position="top" size="small">
        <el-form-item label="Usuário (id)">
          <el-input v-model="concessao.user_id" placeholder="deixe vazio para conceder por tipo"
                    data-test="concessao-user"></el-input>
        </el-form-item>
        <el-form-item label="Tipo de usuário">
          <el-input v-model="concessao.tipo_usuario" placeholder="ex: 1"></el-input>
        </el-form-item>
        <el-form-item label="Instituição (id)">
          <el-input v-model="concessao.instituicao_id"
                    placeholder="vazio = todas as do vínculo"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogConcessao = false">CANCELAR</el-button>
        <el-button class="u-ml-10" type="primary" :loading="concedendo" @click="conceder"
                   data-test="btn-confirmar-concessao">CONCEDER</el-button>
      </span>
    </el-dialog>

    <!-- link -->
    <el-dialog :title="'Gerar link — ' + (painelAtual ? painelAtual.nome : '')"
               :visible.sync="dialogLink" width="520px" append-to-body>
      <el-form label-position="top" size="small">
        <el-form-item label="Validade (horas)">
          <el-input-number v-model="horasLink" :min="1" :max="720"></el-input-number>
        </el-form-item>
      </el-form>
      <el-alert v-if="linkGerado" type="success" :closable="false" show-icon class="u-mt-10">
        <div slot="title">Expira em {{ linkGerado.expira_em }}</div>
        <el-input :value="linkGerado.url" readonly size="small" class="u-mt-5"
                  data-test="link-url"></el-input>
      </el-alert>
      <span slot="footer">
        <el-button @click="dialogLink = false">FECHAR</el-button>
        <el-button class="u-ml-10" type="primary" :loading="gerandoLink" @click="gerarLink">GERAR</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
/**
 * FASE 5 / BLOCO BA-4 — BI 10, 11 e 17.
 *
 * Administração de acesso, distribuição e monitoramento.
 *
 * O aviso sobre "só pode estreitar" não é decoração: é a explicação, para o operador, de por
 * que conceder uma escola fora do vínculo não surte efeito. A regra vive no
 * `BiAcessoService` (interseção, coberta por teste de mutação); a tela apenas a comunica.
 */
import biApi from '../biApi'

export default {
  data () {
    return {
      api: null,
      paineis: [],
      metricas: [],
      acessos: [],
      links: [],
      busca: '',
      carregandoPaineis: false,
      erroPaineis: null,
      carregandoAcessos: false,
      painelAtual: null,
      dialogConcessao: false,
      dialogLink: false,
      concessao: { user_id: '', tipo_usuario: '', instituicao_id: '' },
      concedendo: false,
      gerandoLink: false,
      horasLink: 72,
      linkGerado: null,
      operadores: ['<', '<=', '>', '>=', '=='],
      alerta: { metrica: '', operador: '<', limiar: '', recorrente: false },
      resultadoAlerta: null,
      erroAlerta: null,
      avaliandoAlerta: false
    }
  },

  computed: {
    paineisFiltrados () {
      const q = (this.busca || '').toLowerCase().trim()
      if (!q) return this.paineis
      return this.paineis.filter(function (p) {
        return (p.nome || '').toLowerCase().indexOf(q) !== -1 ||
               (p.item || '').toLowerCase().indexOf(q) !== -1 ||
               (p.slug || '').toLowerCase().indexOf(q) !== -1
      })
    }
  },

  mounted () {
    this.api = biApi.criar({ axios: this.$axios, base: this.base(), auth: this.auth() })
    this.carregarPaineis()
    this.carregarAcessos()
    this.carregarLinks()
    this.carregarMetricas()
  },

  methods: {
    base () { return this.$store.state.config.baseUrlApi },
    auth () { return this.$store.getters.getHeaderAuth },

    /**
     * Normaliza o erro que a API devolve.
     *
     * ARMADILHA REAL deste backend: negativa de permissão vem com HTTP **200** e corpo
     * `{erro: {cod: 800, text: '...'}}` — não com 403. Tratar só `status === 403` nunca
     * dispararia, e atribuir `d.erro` direto renderizaria "[object Object]" na tela.
     * O interceptor global de `main.js` já redireciona 800/801 para `/not-permission`;
     * isto cobre os demais casos e o texto exibido.
     */
    textoErro (erro) {
      if (!erro) return null
      if (typeof erro === 'string') return erro
      if (erro.text) return erro.text
      if (erro.message) return erro.message
      return 'Não foi possível concluir a operação.'
    },

    mensagemErro (e) {
      const r = e && e.response ? e.response : null
      if (!r) return 'Falha de comunicação com o servidor.'
      if (r.status === 403) return 'Você não tem autorização para esta operação.'
      if (r.data && r.data.erro) return this.textoErro(r.data.erro)
      return 'Erro ' + r.status + ' ao consultar o servidor.'
    },

    carregarPaineis () {
      const self = this
      this.carregandoPaineis = true
      this.erroPaineis = null
      this.api.listarPaineis().then(function (r) {
        self.paineis = r.data || []
        self.carregandoPaineis = false
      }).catch(function (e) {
        self.carregandoPaineis = false
        self.erroPaineis = self.mensagemErro(e)
      })
    },

    carregarAcessos () {
      const self = this
      this.carregandoAcessos = true
      this.api.listarAcessos(null).then(function (r) {
        self.acessos = r.data || []
        self.carregandoAcessos = false
      }).catch(function () {
        self.carregandoAcessos = false
        self.acessos = []
      })
    },

    carregarLinks () {
      const self = this
      this.api.links().then(function (r) { self.links = r.data || [] }).catch(function () {})
    },

    carregarMetricas () {
      const self = this
      this.api.listarMetricas({}).then(function (r) { self.metricas = r.data || [] }).catch(function () {})
    },

    abrirConcessao (p) {
      this.painelAtual = p
      this.concessao = { user_id: '', tipo_usuario: '', instituicao_id: '' }
      this.dialogConcessao = true
    },

    conceder () {
      if (this.concedendo) return           // duplo clique não cria duas concessões
      const self = this
      this.concedendo = true
      this.api.conceder(this.painelAtual.slug, this.concessao).then(function (r) {
        self.concedendo = false
        if (r.data && r.data.erro) {
          window.eventBus.$emit('alert', { msg: self.textoErro(r.data.erro), type: 'error' })
          return
        }
        self.dialogConcessao = false
        self.carregarAcessos()
        window.eventBus.$emit('alert', { msg: 'Acesso concedido.', type: 'success' })
      }).catch(function (e) {
        self.concedendo = false
        window.eventBus.$emit('alert', { msg: self.mensagemErro(e), type: 'error' })
      })
    },

    revogar (id) {
      const self = this
      this.api.revogar(id).then(function () { self.carregarAcessos() })
    },

    abrirCompartilhar (p) {
      this.painelAtual = p
      this.linkGerado = null
      this.dialogLink = true
    },

    gerarLink () {
      if (this.gerandoLink) return
      const self = this
      this.gerandoLink = true
      this.api.compartilhar(this.painelAtual.slug, {}, this.horasLink).then(function (r) {
        self.gerandoLink = false
        self.linkGerado = r.data
        self.carregarLinks()
      }).catch(function (e) {
        self.gerandoLink = false
        window.eventBus.$emit('alert', { msg: self.mensagemErro(e), type: 'error' })
      })
    },

    revogarLink (token) {
      const self = this
      this.api.revogarLink(token).then(function () { self.carregarLinks() })
    },

    situacaoLink (l) {
      if (l.revogado_em) return { tipo: 'danger', texto: 'revogado' }
      if (new Date(l.expira_em.replace(' ', 'T')) < new Date()) return { tipo: 'info', texto: 'expirado' }
      return { tipo: 'success', texto: 'ativo' }
    },

    avaliarAlerta () {
      if (this.avaliandoAlerta) return
      const self = this
      this.avaliandoAlerta = true
      this.resultadoAlerta = null
      this.erroAlerta = null

      this.api.alerta(this.alerta.metrica, this.alerta.operador, this.alerta.limiar, {})
        .then(function (r) {
          self.avaliandoAlerta = false
          if (r.data && r.data.erro) { self.erroAlerta = r.data.erro; return }
          self.resultadoAlerta = r.data
        }).catch(function (e) {
          self.avaliandoAlerta = false
          self.erroAlerta = self.mensagemErro(e)
        })
    }
  }
}
</script>

<style scoped>
.estado { padding: 16px; text-align: center; color: #909399; font-size: 12px; }
.estado.erro { color: #F56C6C; }
.chave { font-size: 11px; color: #909399; }
.obs { font-size: 12px; color: #909399; }
</style>
